/* ==========================================================================
   Ratings & Reviews

   The section ships ten real reviews as static HTML so the page has content
   for crawlers and for no-JS. Everything past that lives in a generated data
   file (assets/pdp-reviews-data.js) that is only fetched once the section
   approaches the viewport — it is the last block on the page, so the cost to
   initial load is zero.

   On hydrate the list is re-rendered from the data rather than appended to.
   The first ten rows are the same reviews in the same order, so nothing moves;
   doing it this way means seed markup and rendered markup can never drift.
   ========================================================================== */
(() => {
  const NS = 'pawpaya-reviews';
  if (window[NS]) return;
  window[NS] = true;

  const BATCH = 10;
  const STAR =
    '<svg viewBox="0 0 20 20" fill="currentColor" focusable="false" aria-hidden="true"><path d="M10 1.5l2.6 5.3 5.9.85-4.25 4.15 1 5.85L10 14.9l-5.25 2.75 1-5.85L1.5 7.65l5.9-.85z"/></svg>';

  const el = (tag, cls, text) => {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  };

  function stars(rating, size) {
    const wrap = el('span', `gs-rv__stars gs-rv__stars--${size}`);
    wrap.setAttribute('role', 'img');
    wrap.setAttribute('aria-label', `Rated ${rating} out of 5 stars`);
    const bg = el('span', 'gs-rv__stars-bg');
    bg.innerHTML = STAR.repeat(5);
    const fg = el('span', 'gs-rv__stars-fg');
    fg.innerHTML = STAR.repeat(5);
    fg.style.width = `${(rating / 5) * 100}%`;
    wrap.append(bg, fg);
    return wrap;
  }

  const DAY = 86400000;
  function when(iso) {
    const then = new Date(`${iso}T12:00:00Z`);
    if (Number.isNaN(then.getTime())) return '';
    const days = Math.floor((Date.now() - then.getTime()) / DAY);
    if (days <= 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    if (days < 28) {
      const w = Math.floor(days / 7);
      return w === 1 ? '1 week ago' : `${w} weeks ago`;
    }
    return then.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  }

  function init(root) {
    const src = root.dataset.rvSrc;
    const list = root.querySelector('[data-rv-list]');
    const moreBtn = root.querySelector('[data-rv-more]');
    const moreWrap = root.querySelector('[data-rv-morewrap]');
    const countEl = root.querySelector('[data-rv-count]');
    const chips = [...root.querySelectorAll('[data-rv-filter]')];
    const sortSel = root.querySelector('[data-rv-sort]');
    if (!src || !list) return;

    let DATA = null;
    let view = [];
    let shown = 0;
    const state = { filter: 'all', sort: 'featured' };

    /* ---- photo urls ---------------------------------------------------- */
    const photoUrl = (idx, w) => `${DATA.b}${DATA.p[idx]}&width=${w}`;

    function photos(rec) {
      const idx = rec[4];
      if (idx < 0) return null;
      const wrap = el('div', 'gs-rv__photos');
      const btn = el('button', 'gs-rv__photo');
      btn.type = 'button';
      btn.setAttribute('aria-label', 'Open customer photo full size');
      const img = new Image();
      img.src = photoUrl(idx, 200);
      img.srcset = `${photoUrl(idx, 200)} 200w, ${photoUrl(idx, 400)} 400w`;
      img.sizes = '96px';
      img.loading = 'lazy';
      img.decoding = 'async';
      img.width = 96;
      img.height = 96;
      img.alt = `Customer photo from ${rec[0]}'s review`;
      btn.append(img);
      btn.addEventListener('click', () => openLightbox(rec));
      wrap.append(btn);
      return wrap;
    }

    function row(rec) {
      const [name, rating, date, text] = rec;
      const li = el('li', 'gs-rv__item');

      const head = el('div', 'gs-rv__head');
      head.append(el('p', 'gs-rv__name', name));
      const v = el('span', 'gs-rv__verified');
      v.innerHTML =
        '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M8 1.5l1.9 1.3 2.3-.1.6 2.2 1.8 1.4-1 2.1.4 2.3-2.2.7L10 13.3l-2-.9-2 .9-1.8-1.9-2.2-.7.4-2.3-1-2.1 1.8-1.4.6-2.2 2.3.1z"/><path d="M5.8 8l1.5 1.5 3-3.2"/></svg>';
      v.append(document.createTextNode('Verified Buyer'));
      head.append(v);
      li.append(head);

      const meta = el('div', 'gs-rv__meta');
      meta.append(stars(rating, 16), el('p', 'gs-rv__date', when(date)));
      li.append(meta);

      li.append(el('p', 'gs-rv__text', text));
      const ph = photos(rec);
      if (ph) li.append(ph);
      return li;
    }

    /* ---- filter + sort -------------------------------------------------- */
    function rebuild() {
      const f = state.filter;
      view = DATA.r.filter((r) => {
        if (f === 'all') return true;
        if (f === 'photos') return r[4] >= 0;
        return String(r[1]) === f;
      });
      const byDate = (a, b) => (a[2] < b[2] ? 1 : a[2] > b[2] ? -1 : 0);
      if (state.sort === 'recent') view = view.slice().sort(byDate);
      else if (state.sort === 'high') view = view.slice().sort((a, b) => b[1] - a[1] || byDate(a, b));
      else if (state.sort === 'low') view = view.slice().sort((a, b) => a[1] - b[1] || byDate(a, b));
      // 'featured' keeps the order baked into the data file
      shown = 0;
      list.textContent = '';
      append();
    }

    function append() {
      const slice = view.slice(shown, shown + BATCH);
      const frag = document.createDocumentFragment();
      slice.forEach((r) => frag.append(row(r)));
      list.append(frag);
      shown += slice.length;
      root.classList.toggle('gs-rv--empty', view.length === 0);
      if (moreWrap) moreWrap.hidden = shown >= view.length;
      if (countEl) {
        countEl.textContent = `Showing ${shown.toLocaleString('en-US')} of ${view.length.toLocaleString(
          'en-US'
        )} reviews`;
      }
    }

    /* ---- lightbox ------------------------------------------------------- */
    let lb = null;
    let lastFocus = null;

    function buildLightbox() {
      lb = el('div', 'gs-rv__lb');
      lb.setAttribute('role', 'dialog');
      lb.setAttribute('aria-modal', 'true');
      lb.setAttribute('aria-label', 'Customer photo');
      const card = el('div', 'gs-rv__lb-card');
      const close = el('button', 'gs-rv__lb-close');
      close.type = 'button';
      close.innerHTML = '&times;';
      close.setAttribute('aria-label', 'Close');
      const imgWrap = el('div', 'gs-rv__lb-img');
      const body = el('div', 'gs-rv__lb-body');
      card.append(close, imgWrap, body);
      lb.append(card);
      root.append(lb);
      close.addEventListener('click', closeLightbox);
      lb.addEventListener('click', (e) => {
        if (e.target === lb) closeLightbox();
      });
      document.addEventListener('keydown', (e) => {
        if (!lb.hasAttribute('open')) return;
        if (e.key === 'Escape') closeLightbox();
        // a two-stop focus loop: the close button and the dialog itself
        if (e.key === 'Tab') {
          e.preventDefault();
          close.focus();
        }
      });
      return { imgWrap, body, close };
    }

    let parts = null;
    function openLightbox(rec) {
      if (!parts) parts = buildLightbox();
      lastFocus = document.activeElement;
      parts.imgWrap.textContent = '';
      const img = new Image();
      img.src = photoUrl(rec[4], 1200);
      img.alt = `Customer photo from ${rec[0]}'s review`;
      parts.imgWrap.append(img);

      parts.body.textContent = '';
      const head = el('div', 'gs-rv__head');
      head.append(el('p', 'gs-rv__name', rec[0]));
      parts.body.append(head);
      const meta = el('div', 'gs-rv__meta');
      meta.append(stars(rec[1], 16), el('p', 'gs-rv__date', when(rec[2])));
      parts.body.append(meta, el('p', 'gs-rv__text', rec[3]));

      lb.setAttribute('open', '');
      document.body.style.overflow = 'hidden';
      parts.close.focus();
    }

    function closeLightbox() {
      if (!lb) return;
      lb.removeAttribute('open');
      document.body.style.overflow = '';
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    /* ---- controls ------------------------------------------------------- */
    function syncChips() {
      chips.forEach((c) => c.setAttribute('aria-pressed', String(c.dataset.rvFilter === state.filter)));
    }

    chips.forEach((c) =>
      c.addEventListener('click', () => {
        if (!DATA) return;
        // clicking the active star row clears it, which is what a toggle should do
        const next = c.dataset.rvFilter;
        state.filter = state.filter === next && next !== 'all' ? 'all' : next;
        syncChips();
        rebuild();
      })
    );

    if (sortSel) {
      sortSel.addEventListener('change', () => {
        if (!DATA) return;
        state.sort = sortSel.value;
        rebuild();
      });
    }

    if (moreBtn) {
      moreBtn.addEventListener('click', () => {
        if (!DATA) return;
        append();
      });
    }

    /* ---- hydrate -------------------------------------------------------- */
    let started = false;
    function hydrate() {
      if (started) return;
      started = true;
      const s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = () => {
        DATA = window.PAWPAYA_REVIEWS;
        if (!DATA || !DATA.r) return;
        root.querySelectorAll('[data-rv-skeleton]').forEach((n) => n.remove());
        syncChips();
        rebuild();
      };
      s.onerror = () => {
        // leave the ten seeded reviews in place; the section still reads correctly
        root.querySelectorAll('[data-rv-skeleton]').forEach((n) => n.remove());
        if (moreWrap) moreWrap.hidden = true;
      };
      document.head.append(s);
    }

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            io.disconnect();
            hydrate();
          }
        },
        { rootMargin: '800px 0px' }
      );
      io.observe(root);
    } else {
      hydrate();
    }

    // a control touched before the observer fires still works
    [...chips, sortSel, moreBtn].forEach((n) => n && n.addEventListener('pointerenter', hydrate, { once: true }));
  }

  const start = () => document.querySelectorAll('[data-rv-root]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
